// Per-item discount limits on a bill line. The item master's min / max
// discount % reaches billing with the stock (/v1/inventory/stock/items) as
// minDiscountPercentage / maxDiscountPercentage, both 0 when not set. With
// manual batching it comes with the batch list (/product/itemBatch/
// getBatchNumberItemId) as item_min_discount_percentage /
// item_max_discount_percentage, kept on the line under the same names.
//
// Like the backend's own check, the limits bound a discount that is given: a
// line with no discount (0 / blank) is left alone, while any discount is held
// between the item's minimum and maximum.

const MAX_NAMED_ITEMS = 3;

const percentText = (value) => `${Number(Number(value).toFixed(2))}%`;

/** The line's { min, max } discount %, or null when the item has no maximum set. */
export function itemDiscountLimits(row) {
  const max = parseFloat(row?.maxDiscountPercentage);
  if (!(max > 0)) return null;
  const cappedMax = Math.min(max, 100);
  const min = parseFloat(row?.minDiscountPercentage);
  return { min: Math.min(Math.max(0, Number.isNaN(min) ? 0 : min), cappedMax), max: cappedMax };
}

/**
 * Holds a given discount % inside the limits.
 * @returns { value, adjusted } — `adjusted` is "min" / "max" when the discount
 *   had to move to that limit (value is then the limit), else null (value is
 *   the discount as given).
 */
export function clampDiscountToLimits(discount, limits) {
  const value = parseFloat(discount);
  if (!limits || !(value > 0)) return { value: discount, adjusted: null };
  if (value > limits.max) return { value: limits.max, adjusted: "max" };
  if (value < limits.min) return { value: limits.min, adjusted: "min" };
  return { value: discount, adjusted: null };
}

/**
 * Checks a new line against the item's limits, which only arrive with the
 * stock API response. The typed discount is what counts — the API can ignore
 * one outside the limits and price at its own discount instead (typed 40% of
 * a 10–20% item came back at 10%). With nothing typed, the discount the API
 * priced at is checked. Returns { value, adjusted, limits } — the discount to
 * price at instead — or null when it is fine.
 */
export function stockDiscountOutsideLimits(itemDetails, typedDiscount) {
  const detail = itemDetails?.[0];
  const limits = itemDiscountLimits(detail);
  const discount =
    parseFloat(typedDiscount) > 0 ? typedDiscount : detail?.item_discount_percentage;
  const { value, adjusted } = clampDiscountToLimits(discount, limits);
  return adjusted ? { value, adjusted, limits } : null;
}

/**
 * The header discount % a line can take on top of its own discount — the two
 * compound (10% then 10% is 19%) — so its total discount stays inside the
 * limits. A cleared header discount (0) is left alone.
 * @returns { headerPer, adjusted } — adjusted is "min" / "max" when the header
 *   discount had to change for this line, else null.
 */
export function headerDiscountWithinLimits(linePer, headerPer, limits) {
  const header = parseFloat(headerPer) || 0;
  if (!limits || header <= 0) return { headerPer: header, adjusted: null };
  const line = Math.min(100, Math.max(0, parseFloat(linePer) || 0));
  const total = 100 - ((100 - line) * (100 - header)) / 100;
  let adjusted = null;
  if (total > limits.max) adjusted = "max";
  else if (total < limits.min) adjusted = "min";
  if (!adjusted) return { headerPer: header, adjusted: null };
  if (line >= 100) return { headerPer: 0, adjusted };
  const target = adjusted === "max" ? limits.max : limits.min;
  const needed = (1 - (100 - target) / (100 - line)) * 100;
  return { headerPer: Math.max(0, needed), adjusted };
}

/** Toast text for a line discount moved to a limit. */
export function discountLimitMessage(itemName, limits, adjusted) {
  const bound = adjusted === "max" ? limits.max : limits.min;
  const subject = itemName ? `${itemName}: discount` : "Discount";
  return `${subject} must be between ${percentText(limits.min)} and ${percentText(
    limits.max
  )}. Set to ${percentText(bound)}.`;
}

/**
 * Toast text for a header discount held within item limits.
 * @param limited [{ name, limits, adjusted }] one per line it was changed on
 */
export function headerLimitMessage(limited) {
  const named = limited
    .slice(0, MAX_NAMED_ITEMS)
    .map(
      ({ name, limits, adjusted }) =>
        `${name} (${adjusted} ${percentText(adjusted === "max" ? limits.max : limits.min)})`
    )
    .join(", ");
  const more =
    limited.length > MAX_NAMED_ITEMS ? ` and ${limited.length - MAX_NAMED_ITEMS} more` : "";
  return `Header discount kept within each item's discount limit: ${named}${more}.`;
}
